import { REMOTE_DATA_STATUS, RemoteData } from '@nextTypes/remoteData';

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

export const notAsked = (): RemoteData<never, never> => ({
  type: REMOTE_DATA_STATUS.NOT_ASKED,
});

export const loading = (): RemoteData<never, never> => ({
  type: REMOTE_DATA_STATUS.LOADING,
});

export const failure = (error: unknown): RemoteData<string, never> => ({
  type: REMOTE_DATA_STATUS.FAILURE,
  error: getErrorMessage(error),
});

export const success = <D>(data: D): RemoteData<never, D> => ({
  type: REMOTE_DATA_STATUS.SUCCESS,
  data,
});

/**
 * Select a property from a RemoteData object, returning the property as a new
 * RemoteData object.
 *
 * This is useful when you need to access a nested property in a RemoteData
 * object but still want access to the data state, i.e. loading, error, success
 * or failure.
 *
 * @param remoteData - The RemoteData object to select from.
 * @param selector - The selector function to apply to the data property of the
 * RemoteData object.
 * @returns A new RemoteData object with the selected property.
 */
export const remoteDataSelect = <E, D, T>(
  remoteData: RemoteData<E, D>,
  selector: (data: D) => T,
): RemoteData<E, T> => {
  switch (remoteData.type) {
    case REMOTE_DATA_STATUS.NOT_ASKED:
    case REMOTE_DATA_STATUS.LOADING:
    case REMOTE_DATA_STATUS.FAILURE:
      return remoteData;

    case REMOTE_DATA_STATUS.SUCCESS:
      return success(selector(remoteData.data));

    default: {
      const _exhaustiveCheck: never = remoteData;
      return _exhaustiveCheck;
    }
  }
};
